import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const volunteerApplicationSchema = z.object({
  firstName: z.string().min(2, "First name must be at least 2 characters"),
  lastName: z.string().min(2, "Last name must be at least 2 characters"),
  email: z.string().email("Please enter a valid email address"),
  phone: z.string().min(10, "Please enter a valid phone number"),
  address: z.string().min(5, "Please enter your address"),
  city: z.string().min(2, "Please enter your city"),
  state: z.string().min(2, "Please enter your state"),
  zipCode: z.string().min(5, "Please enter your zip code"),
  dateOfBirth: z.string().min(1, "Please enter your date of birth"),
  emergencyContact: z.string().min(2, "Please enter emergency contact name"),
  emergencyPhone: z.string().min(10, "Please enter emergency contact phone"),
  availability: z.array(z.string()).min(1, "Please select your availability"),
  skills: z.string().min(10, "Please describe your skills"),
  interests: z.array(z.string()).min(1, "Please select your areas of interest"),
  experience: z.string().min(10, "Please describe your experience"),
  motivation: z.string().min(10, "Please tell us why you want to volunteer"),
  timeCommitment: z.enum(["few-hours-month", "few-hours-week", "several-hours-week", "part-time", "flexible"]),
  backgroundCheck: z.boolean().refine(val => val === true, {
    message: "You must consent to a background check."
  }),
  references: z.string().min(10, "Please provide reference information"),
  consent: z.boolean().refine(val => val === true, {
    message: "You must consent to the volunteer terms."
  }),
});

type VolunteerApplicationValues = z.infer<typeof volunteerApplicationSchema>;

export default function VolunteerApplicationForm() {
  const { toast } = useToast();
  const [isSubmitSuccess, setIsSubmitSuccess] = useState(false);

  const form = useForm<VolunteerApplicationValues>({
    resolver: zodResolver(volunteerApplicationSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      address: "",
      city: "",
      state: "",
      zipCode: "",
      dateOfBirth: "",
      emergencyContact: "",
      emergencyPhone: "",
      availability: [],
      skills: "",
      interests: [],
      experience: "",
      motivation: "",
      references: "",
      backgroundCheck: false,
      consent: false,
    },
  });

  const submitVolunteerApplicationMutation = useMutation({
    mutationFn: async (data: VolunteerApplicationValues) => {
      const response = await apiRequest("POST", "/api/volunteer-application", data);
      return response.json();
    },
    onSuccess: () => {
      setIsSubmitSuccess(true);
      toast({
        title: "Volunteer application submitted successfully",
        description: "Our volunteer coordinator will contact you within 3-5 business days.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error submitting application",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  function onSubmit(data: VolunteerApplicationValues) {
    submitVolunteerApplicationMutation.mutate(data);
  }

  if (isSubmitSuccess) {
    return (
      <div className="bg-white p-8 rounded-lg shadow-md max-w-2xl mx-auto">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 mb-6 rounded-full bg-green-100">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Application Submitted!</h2>
          <p className="text-lg text-slate-600 mb-6">
            Thank you for your interest in volunteering with StaffAbility. Our volunteer coordinator will contact you within 3-5 business days to discuss opportunities and next steps.
          </p>
          <Button asChild>
            <a href="/contact">Back to Contact</a>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-8 rounded-lg shadow-md max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold mb-6 text-slate-900">Volunteer Application</h2>
      <p className="text-slate-600 mb-8">
        Join our mission to provide second chances and support justice-involved individuals in their journey to meaningful employment. Your skills and passion can make a real difference.
      </p>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>First Name</FormLabel>
                  <FormControl>
                    <Input placeholder="First name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Last Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Last name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input placeholder="your.email@example.com" type="email" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone Number</FormLabel>
                  <FormControl>
                    <Input placeholder="(555) 123-4567" type="tel" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="address"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Address</FormLabel>
                <FormControl>
                  <Input placeholder="Street address" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <FormField
              control={form.control}
              name="city"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>City</FormLabel>
                  <FormControl>
                    <Input placeholder="City" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="state"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>State</FormLabel>
                  <FormControl>
                    <Input placeholder="State" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="zipCode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Zip Code</FormLabel>
                  <FormControl>
                    <Input placeholder="12345" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField
              control={form.control}
              name="dateOfBirth"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Date of Birth</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="timeCommitment"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Time Commitment</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="How much time can you commit?" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="few-hours-month">A few hours per month</SelectItem>
                      <SelectItem value="few-hours-week">A few hours per week</SelectItem>
                      <SelectItem value="several-hours-week">Several hours per week</SelectItem>
                      <SelectItem value="part-time">Part-time commitment</SelectItem>
                      <SelectItem value="flexible">Flexible/as needed</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField
              control={form.control}
              name="emergencyContact"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Emergency Contact Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Emergency contact name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="emergencyPhone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Emergency Contact Phone</FormLabel>
                  <FormControl>
                    <Input placeholder="(555) 123-4567" type="tel" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="availability"
            render={() => (
              <FormItem>
                <div className="mb-4">
                  <FormLabel className="text-base">Availability (Select all that apply)</FormLabel>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { id: "weekday-mornings", label: "Weekday Mornings" },
                    { id: "weekday-afternoons", label: "Weekday Afternoons" },
                    { id: "weekday-evenings", label: "Weekday Evenings" },
                    { id: "weekend-mornings", label: "Weekend Mornings" },
                    { id: "weekend-afternoons", label: "Weekend Afternoons" },
                    { id: "weekend-evenings", label: "Weekend Evenings" },
                    { id: "flexible", label: "Flexible" },
                    { id: "special-events", label: "Special Events" },
                  ].map((item) => (
                    <FormField
                      key={item.id}
                      control={form.control}
                      name="availability"
                      render={({ field }) => {
                        return (
                          <FormItem
                            key={item.id}
                            className="flex flex-row items-start space-x-3 space-y-0"
                          >
                            <FormControl>
                              <Checkbox
                                checked={field.value?.includes(item.id)}
                                onCheckedChange={(checked) => {
                                  return checked
                                    ? field.onChange([...field.value, item.id])
                                    : field.onChange(
                                        field.value?.filter(
                                          (value) => value !== item.id
                                        )
                                      )
                                }}
                              />
                            </FormControl>
                            <FormLabel className="text-sm font-normal">
                              {item.label}
                            </FormLabel>
                          </FormItem>
                        )
                      }}
                    />
                  ))}
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="interests"
            render={() => (
              <FormItem>
                <div className="mb-4">
                  <FormLabel className="text-base">Areas of Interest (Select all that apply)</FormLabel>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { id: "mentoring", label: "One-on-one Mentoring" },
                    { id: "job-coaching", label: "Job Coaching & Interview Prep" },
                    { id: "skills-training", label: "Skills Training Workshops" },
                    { id: "resume-help", label: "Resume & Cover Letter Help" },
                    { id: "transportation", label: "Transportation Assistance" },
                    { id: "clothing-closet", label: "Professional Clothing Closet" },
                    { id: "admin-support", label: "Administrative Support" },
                    { id: "event-planning", label: "Event Planning & Coordination" },
                    { id: "fundraising", label: "Fundraising & Development" },
                    { id: "social-media", label: "Social Media & Marketing" },
                    { id: "research", label: "Research & Data Analysis" },
                    { id: "legal-assistance", label: "Legal Assistance" },
                  ].map((item) => (
                    <FormField
                      key={item.id}
                      control={form.control}
                      name="interests"
                      render={({ field }) => {
                        return (
                          <FormItem
                            key={item.id}
                            className="flex flex-row items-start space-x-3 space-y-0"
                          >
                            <FormControl>
                              <Checkbox
                                checked={field.value?.includes(item.id)}
                                onCheckedChange={(checked) => {
                                  return checked
                                    ? field.onChange([...field.value, item.id])
                                    : field.onChange(
                                        field.value?.filter(
                                          (value) => value !== item.id
                                        )
                                      )
                                }}
                              />
                            </FormControl>
                            <FormLabel className="text-sm font-normal">
                              {item.label}
                            </FormLabel>
                          </FormItem>
                        )
                      }}
                    />
                  ))}
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="skills"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Skills and Qualifications</FormLabel>
                <FormControl>
                  <Textarea 
                    placeholder="Please describe your relevant skills, qualifications, and any special abilities you'd like to contribute..." 
                    rows={4} 
                    {...field} 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="experience"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Relevant Experience</FormLabel>
                <FormControl>
                  <Textarea 
                    placeholder="Please describe any relevant volunteer or professional experience, especially working with justice-involved individuals or similar populations..." 
                    rows={4} 
                    {...field} 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="motivation"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Why do you want to volunteer with StaffAbility?</FormLabel>
                <FormControl>
                  <Textarea 
                    placeholder="Tell us what motivates you to volunteer and what you hope to contribute to our mission..." 
                    rows={4} 
                    {...field} 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="references"
            render={({ field }) => (
              <FormItem>
                <FormLabel>References</FormLabel>
                <FormControl>
                  <Textarea 
                    placeholder="Please provide 2-3 references (name, relationship, and contact information) who can speak to your character and reliability..." 
                    rows={4} 
                    {...field} 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="backgroundCheck"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel>
                    I understand that volunteering with StaffAbility requires a background check and I consent to this process.
                  </FormLabel>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="consent"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel>
                    I consent to StaffAbility collecting and using this information for volunteer screening and coordination purposes.
                  </FormLabel>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button 
            type="submit" 
            className="w-full"
            disabled={submitVolunteerApplicationMutation.isPending}
          >
            {submitVolunteerApplicationMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Submitting Application...
              </>
            ) : (
              "Submit Volunteer Application"
            )}
          </Button>
        </form>
      </Form>
    </div>
  );
}